import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { ReportConfig } from '../../data/reports';
import { SavedTemplate } from '../../context/AppContext';
interface SaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  config: ReportConfig;
  existingTemplate?: SavedTemplate;
  onSaved: (id: string) => void;
  saveTemplate: (name: string, description: string, config: ReportConfig, existingId?: string) => string;
}
export function SaveTemplateDialog({
  open,
  onClose,
  config,
  existingTemplate,
  onSaved,
  saveTemplate
}: SaveTemplateDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'update' | 'new'>('update');
  const [error, setError] = useState('');
  useEffect(() => {
    if (open) {
      setName(existingTemplate?.name ?? '');
      setDescription(existingTemplate?.description ?? '');
      setMode(existingTemplate ? 'update' : 'new');
      setError('');
    }
  }, [open, existingTemplate]);
  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please give your template a name.');
      return;
    }
    const id = saveTemplate(name.trim(), description.trim(), config, mode === 'update' && existingTemplate ? existingTemplate.id : undefined);
    onSaved(id);
  };
  return <AnimatePresence data-id="element-72">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-40 bg-navy-950/30 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" data-id="element-73" />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-74">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 8
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 8
        }} transition={{
          duration: 0.18
        }} role="dialog" aria-modal="true" aria-labelledby="save-template-title" className="card-float-lg pointer-events-auto w-full max-w-md rounded-2xl bg-white" data-id="element-75">
              <div className="flex items-center justify-between border-b border-navy-50 px-5 py-4" data-id="element-76">
                <h2 id="save-template-title" className="text-sm font-semibold text-navy-900" data-id="element-77">
                  {existingTemplate ? 'Save Template' : 'Save as Template'}
                </h2>
                <button type="button" onClick={onClose} aria-label="Close dialog" className="flex h-8 w-8 items-center justify-center rounded-full text-navy-400 transition-colors hover:bg-navy-50 hover:text-navy-900" data-id="element-78">
                  <XIcon className="h-4 w-4" data-id="element-79" />
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-4 px-5 py-4" data-id="element-80">
                {existingTemplate && <div className="flex rounded-full bg-navy-50 p-0.5" role="radiogroup" aria-label="Save mode" data-id="element-81">
                    <button type="button" role="radio" aria-checked={mode === 'update'} onClick={() => setMode('update')} className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${mode === 'update' ? 'bg-navy-900 text-white' : 'text-navy-500 hover:text-navy-900'}`} data-id="element-82">
                      Update "{existingTemplate.name}"
                    </button>
                    <button type="button" role="radio" aria-checked={mode === 'new'} onClick={() => setMode('new')} className={`flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${mode === 'new' ? 'bg-navy-900 text-white' : 'text-navy-500 hover:text-navy-900'}`} data-id="element-83">
                      Save as new
                    </button>
                  </div>}
                <div data-id="element-84">
                  <label htmlFor="tpl-name" className="block text-xs font-medium text-navy-600" data-id="element-85">
                    Template name
                  </label>
                  <input id="tpl-name" type="text" value={name} onChange={e => {
                setName(e.target.value);
                setError('');
              }} placeholder="e.g. Monthly No-Show Summary" className={`mt-1.5 h-9 w-full rounded-lg border px-3 text-sm text-navy-900 placeholder:text-navy-300 focus:outline-none focus:ring-2 ${error ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-500/20' : 'border-navy-100 focus:border-navy-300 focus:ring-navy-900/10'}`} data-id="element-86" />
                  {error && <p className="mt-1 text-xs text-rose-600" data-id="element-87">{error}</p>}
                </div>
                <div data-id="element-88">
                  <label htmlFor="tpl-desc" className="block text-xs font-medium text-navy-600" data-id="element-89">
                    Description{' '}
                    <span className="text-navy-300" data-id="element-90">(optional)</span>
                  </label>
                  <textarea id="tpl-desc" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="What does this report show?" className="mt-1.5 w-full rounded-lg border border-navy-100 px-3 py-2 text-sm text-navy-900 placeholder:text-navy-300 focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-900/10" data-id="element-91" />
                </div>
                <div className="flex justify-end gap-2 pt-1" data-id="element-92">
                  <button type="button" onClick={onClose} className="h-9 rounded-full border border-navy-100 px-4 text-sm font-medium text-navy-600 transition-colors hover:border-navy-300 hover:text-navy-900" data-id="element-93">
                    Cancel
                  </button>
                  <button type="submit" className="h-9 rounded-full bg-navy-900 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-800" data-id="element-94">
                    {mode === 'update' && existingTemplate ? 'Update Template' : 'Save Template'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}